import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import {
  TedLessonPlannerResponseDto,
  TedLessonPlannerSteps,
} from "@packages/api/madlenAPI.schemas";
import { TFunction } from "i18next";

// Dosyayı kaydetmek için bir yardımcı fonksiyon
export const saveFile = (blob: Blob, filename: string) => {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

// Template'i veriyle dolduran fonksiyon
export const fillTemplate = (
  templateBuffer: ArrayBuffer,
  data: Record<string, unknown>,
) => {
  try {
    const zip = new PizZip(templateBuffer);
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      delimiters: {
        start: "{{",
        end: "}}",
      },
    });

    doc.render(data);

    // Post-processing: XML içeriğini al ve justify alignment'ları left'e çevir
    const docxZip = doc.getZip();

    // document.xml dosyasını bul ve düzenle
    const documentXml = docxZip.file("word/document.xml");
    if (documentXml) {
      let content = documentXml.asText();

      // Justify alignment'ları left alignment'a çevir
      // <w:jc w:val="both"/> -> <w:jc w:val="left"/>
      content = content.replace(
        /<w:jc w:val="both"\/>/g,
        '<w:jc w:val="left"/>',
      );
      content = content.replace(
        /<w:jc w:val="distribute"\/>/g,
        '<w:jc w:val="left"/>',
      );

      // Güncellenmiş içeriği geri yaz
      docxZip.file("word/document.xml", content);
    }

    const out = docxZip.generate({
      type: "blob",
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    return out;
  } catch (error) {
    console.error("Docxtemplater error: ", error);
    throw error;
  }
};

// TED plan verilerini template formatına dönüştüren fonksiyon
export const transformTedPlanToTemplateData = (
  t: TFunction,
  plan: TedLessonPlannerResponseDto,
) => {
  // Adımları ders numarasına göre grupla
  const stepsByLesson: { [key: number]: TedLessonPlannerSteps[] } = {};
  const maarifLearningOutcomes = plan.maarifLearningOutcomes?.units || [];

  // Adımları ders numarasına göre grupla
  const steps = plan.steps || [];
  steps.forEach((step) => {
    if (step.ders_no) {
      if (!stepsByLesson[step.ders_no]) {
        stepsByLesson[step.ders_no] = [];
      }
      stepsByLesson[step.ders_no].push(step);
    }
  });

  // Roma rakamları
  const toRoman = (num: number): string => {
    const romanNumerals = [
      { value: 10, numeral: "X" },
      { value: 9, numeral: "IX" },
      { value: 5, numeral: "V" },
      { value: 4, numeral: "IV" },
      { value: 1, numeral: "I" },
    ];

    let result = "";
    for (const { value, numeral } of romanNumerals) {
      while (num >= value) {
        result += numeral;
        num -= value;
      }
    }
    return result;
  };

  // Ders numaralarını sırala
  const sortedLessonNumbers = Object.keys(stepsByLesson)
    .map((num) => parseInt(num))
    .sort((a, b) => a - b);

  let stepsData = "";
  let stepCounter = 1;

  sortedLessonNumbers.forEach((lessonNumber, index) => {
    const lessonSteps = stepsByLesson[lessonNumber];
    const roman = toRoman(index + 1);

    // Ders başlığını oluştur
    let lessonTitle = "";
    if (lessonNumber === 1) {
      lessonTitle = "BİRİNCİ DERSTE";
    } else if (lessonNumber === 2) {
      lessonTitle = "İKİNCİ DERSTE";
    } else if (lessonNumber === 3) {
      lessonTitle = "ÜÇÜNCÜ DERSTE";
    } else if (lessonNumber === 4) {
      lessonTitle = "DÖRDÜNCÜ DERSTE";
    } else if (lessonNumber === 5) {
      lessonTitle = "BEŞİNCİ DERSTE";
    } else if (lessonNumber === 6) {
      lessonTitle = "ALTİNCİ DERSTE";
    } else if (lessonNumber === 7) {
      lessonTitle = "YEDİNCİ DERSTE";
    } else if (lessonNumber === 8) {
      lessonTitle = "SEKİZİNCİ DERSTE";
    } else if (lessonNumber === 9) {
      lessonTitle = "DOKUZİNCİ DERSTE";
    } else if (lessonNumber === 10) {
      lessonTitle = "ONİKİNCİ DERSTE";
    } else if (lessonNumber === 11) {
      lessonTitle = "ONBİRİNCİ DERSTE";
    } else {
      lessonTitle = `${lessonNumber}. DERSTE`;
    }

    // Eğer ders öncesi hazırlık adımları varsa
    if (
      index === 0 &&
      lessonSteps.some(
        (step) =>
          step.zaman_dilimi?.includes("ÖNCE") ||
          step.icerik?.includes("hazırlık") ||
          step.icerik?.includes("önceden"),
      )
    ) {
      stepsData += `${roman}- BİRİNCİ DERSTEN ÖNCE\n`;
    } else {
      stepsData += `${roman}- ${lessonTitle}`;

      // Toplam süreyi ve yöntem/tekniği ekle
      const totalDuration = lessonSteps.reduce(
        (total, step) => total + (step.sure_dakika || 0),
        0,
      );
      const methods = [
        ...new Set(
          lessonSteps.map((step) => step.yontem_teknik).filter(Boolean),
        ),
      ];

      if (totalDuration > 0 || methods.length > 0) {
        stepsData += "\n";
        if (methods.length > 0) {
          stepsData += `${methods.join(" - ")}`;
        }
        if (totalDuration > 0) {
          stepsData += ` (${totalDuration} Dakika)`;
        }
      }
      stepsData += "\n";
    }

    // Her adımı ekle
    lessonSteps.forEach((step) => {
      if (step.icerik) {
        stepsData += `${stepCounter}. ${step.icerik}\n`;
        stepCounter++;
      }
    });

    stepsData += "\n";
  });

  // Gereksiz boşlukları temizle
  stepsData = stepsData.trim();

  return {
    GRADE: plan.grade,
    TEMA: t("courseType." + plan.courseName),
    icerik_cercevesi:
      maarifLearningOutcomes
        .map((outcome) => outcome?.icerikCercevesiList?.join(", ") || "")
        .join(", ") || "-",
    anahtar_kavramlar:
      maarifLearningOutcomes
        .map((outcome) => outcome?.anahtarKavramlar || "")
        .join(", ") || "-",
    genellemeler_ilkeler: "-",
    semboller_gosterimler: "-",
    ogrenme_ciktilari_ve_surec_bilesenleri:
      maarifLearningOutcomes
        .map(
          (outcome) =>
            outcome?.ogrenmeCiktilariVeSurecBilesenleriList?.join(", ") || "",
        )
        .join("\n") || "-",
    alan_becerileri:
      maarifLearningOutcomes
        .map((outcome) => outcome?.alanBecerileri || "")
        .join(", ") || "-",
    kavramsal_beceriler:
      maarifLearningOutcomes
        .map((outcome) => outcome?.kavramsalBeceriler || "")
        .join("\n") || "-",
    sosyal_duygusal_ogrenme_beceriler:
      maarifLearningOutcomes
        .map((outcome) => outcome?.sosyalDuygusalOgrenmeBecerileri || "")
        .join("\n") || "-",
    degerler:
      maarifLearningOutcomes
        .map((outcome) => outcome?.degerler || "")
        .join("\n") || "-",
    okuryazarlık_beceerileri:
      maarifLearningOutcomes
        .map((outcome) => outcome?.okuryazarlikBecerileri || "")
        .join("\n") || "-",
    disiplinler_arasi_iliskiler_ders:
      maarifLearningOutcomes
        .map((outcome) => outcome?.disiplinlerArasiIliskiler || "")
        .join(", ") || "-",

    beceriler_arasi_iliskiler:
      maarifLearningOutcomes
        .map((outcome) => outcome?.becerilerArasiIliskiler || "")
        .join(", ") || "-",

    egilimler:
      maarifLearningOutcomes
        .map((outcome) => outcome?.egilimler || "")
        .join(", ") || "-",

    strateji_yontem_teknik: "-",
    egitim_teknolojiler_arac_ve_gerecler: "-",

    ders_planinin_amaci:
      maarifLearningOutcomes
        .map((outcome) => outcome?.description || "")
        .join(", ") || "-",
    // TODO
    ogrenme_kanitlari_olcme_ve_degerlendirme: "-",
    ogretme_ogrenme_yasantilari_temel_kabuller:
      maarifLearningOutcomes
        .map((outcome) => outcome?.temelKabuller || "")
        .join(", ") || "-",
    ogretme_ogrenme_yasantilari_on_degerlendirme_sureci:
      maarifLearningOutcomes
        .map((outcome) => outcome?.onDegerlendirmeSureci || "")
        .join(", ") || "-",

    ogretme_ogrenme_yasantilari_kopru_kurma:
      maarifLearningOutcomes
        .map((outcome) => outcome?.temelKabuller || "")
        .join(", ") || "-",

    ders_n_content: stepsData,
    DATE: new Date().toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
  };
};
